import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useNCallContext } from "../../context/ncall-context";
import Loader from "../../components/loader";
import { localStorageSetter } from "../../utils/localStorageHelper";
import Modal from "../../components/modal/modal";
import { API } from "../../service/api";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import NoData from "../../components/noData";
import Table from "../../components/table";
import ConfirmationModal from "../../components/modal/confirmationModal";

const config = {
  title: "",
  handler: null,
};

const Clients = () => {
  const { state, openModal, setOpenModal, APP_RESET, getAllClients, dispatch } =
    useNCallContext();
  const { clients } = state;
  const confirmationmodalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("logged-in-as") == "client") {
      navigate("/client");
    }
    localStorage.setItem("sort", JSON.stringify({ id: "code", desc: false }));
  }, []);

  let _id = "";
  const companyNameRef = useRef();
  const codeRef = useRef();
  const clientIdRef = useRef();

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "companyName",
      },
      {
        Header: "Code",
        accessor: "code",
      },
      {
        Header: "Client Id",
        accessor: "ncall_ClientId",
      },
    ],
    []
  );

  const handleAddClient = async () => {
    let data = {
      companyName: companyNameRef.current.value,
      ncall_ClientId: clientIdRef.current.value,
      code: codeRef.current.value,
      shifts: "",
    };
    try {
      const res = await API.postNewCompany(data);

      if (res.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast(res.message);
        getAllClients();
      }
    } catch (error) {
      showErrorToast(error);
    }
  };

  const viewHandler = (client) => {
    APP_RESET();

    localStorageSetter({
      code: client.code,
      companyName: client.companyName,
      clientId: client.ncall_ClientId,
      companyId: client.id,
    });

    navigate("client-detail");
  };

  const handleUpdateBtnClk = (client) => {
    _id = client.id;

    companyNameRef.current = client.companyName;
    codeRef.current = client.code;
    clientIdRef.current = client.ncall_ClientId;

    config.title = "Update Client";
    config.handler = handleUpdateClient;

    setOpenModal(!openModal);
  };

  const handleUpdateClient = async () => {
    let data = {
      companyName: companyNameRef.current.value,
      ncall_ClientId: clientIdRef.current.value,
      code: codeRef.current.value,
      shifts: "",
    };

    try {
      const res = await API.putCompany(_id, data);

      if (res.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast(res.message);
        getAllClients();
      }
    } catch (error) {
      showErrorToast(error);
    }
  };

  const openDeletionConfirmationModal = (client) => {
    if (confirmationmodalRef) {
      confirmationmodalRef.current.handleModalOpen();
      confirmationmodalRef.current.setRecordForModal(client);
      confirmationmodalRef.current.setHandlerMethod(() => handleDeleteBtnClk);
    }
  };

  const handleDeleteBtnClk = async (client) => {
    try {
      const res = await API.deleteClient(client.id);
      if (res.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast(res.message);
      }
      getAllClients();
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  const actions = [
    {
      icon: EyeIcon,
      handler: viewHandler,
      styles: "text-indigo-500 w-5 h-5  inline",
    },
    {
      icon: PencilIcon,
      handler: handleUpdateBtnClk,
      styles: "w-5 h-5 text-green-500",
    },
    {
      icon: TrashIcon,
      handler: openDeletionConfirmationModal,
      styles: "w-5 h-5 text-red-500",
    },
  ];

  if (state.loading) return <Loader />;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {<ConfirmationModal ref={confirmationmodalRef} />}

      {openModal ? (
        <Modal config={config}>
          <div className="space-y-6 sm:space-y-5">
            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4  sm:border-gray-200 sm:pt-5">
              <label
                htmlFor="employee"
                className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
              >
                Company Name
              </label>

              <div className="mt-1 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  ref={companyNameRef}
                  required
                  defaultValue={companyNameRef.current}
                  id="employee"
                  name="employee"
                  className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:max-w-xs sm:text-sm"
                />
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4  sm:border-gray-200 sm:pt-5">
              <label
                htmlFor="department"
                className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
              >
                Client Id
              </label>
              <div className="mt-1 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  ref={clientIdRef}
                  required
                  defaultValue={clientIdRef.current}
                  name="department"
                  className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:max-w-xs sm:text-sm"
                />
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4  sm:border-gray-200 sm:pt-5">
              <label
                htmlFor="shift"
                className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
              >
                Code
              </label>
              <div className="mt-1 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  ref={codeRef}
                  required
                  defaultValue={codeRef.current}
                  id="shift"
                  name="shift"
                  className="block uppercase w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:max-w-xs sm:text-sm"
                />
              </div>
            </div>
          </div>
        </Modal>
      ) : (
        ""
      )}

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            All Clients
          </h1>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => {
              config.title = "Add New Client";
              config.handler = handleAddClient;
              setOpenModal(!openModal);
            }}
            type="button"
            className="block rounded-md bg-indigo-600 py-2 px-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Add Client
          </button>
        </div>
      </div>
      {clients.length == 0 ? (
        <NoData message={"You don't have any Clients."} />
      ) : (
        <div className="mt-8 flow-root">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <Table
                columns={columns}
                data={state?.clients}
                actions={actions}
                name="clients"
              />

              {/* <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >
                      #
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Code
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Client Id
                    </th>

                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                    >
                      <span className="sr-only">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {state?.clients.map((client, idx) => (
                    <tr
                      key={client.id}
                      className={idx % 2 === 0 ? undefined : "bg-gray-50"}
                    >
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {idx + 1}
                      </td>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {client.companyName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {client.code}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {client.ncall_ClientId}
                      </td>

                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <button
                          className="text-indigo-500 w-5 h-5  inline"
                          onClick={() => viewHandler(client)}
                        >
                          <EyeIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
